import { useContext, useEffect, useState } from 'react';
import axios from '@helpers/axios';
import { secondsToDaysAndHours } from '@helpers/utils';
import signOut from '@helpers/signOut';
import * as Sentry from '@sentry/browser';
import { UserContext } from '@services/Session';
import { useHistory } from 'react-router-dom';

// Types
interface InvitingUser {
  nameFirst: string;
  nameLast: string;
  email: string;
}

interface Invite {
  teamRole: string;
  expires: number;
  invitedUserId: string;
  invitingUser: InvitingUser;
  teamName: string;
  playerName: string;
}

interface Expiration {
  days: number;
  hours: number;
}

export const useInvite = (inviteCode: string) => {
  // Constants
  const userStore = useContext(UserContext);
  const history = useHistory();

  // States
  const [error, setError] = useState(null);
  const [expiration, setExpiration] = useState<Expiration | null>(null);
  const [invite, setInvite] = useState<Invite | null>(null);
  const [invitingUser, setInvitingUser] = useState<InvitingUser | null>(null);
  const [loading, toggleLoading] = useState(true);
  const [nameToManage, setNameToManage] = useState<string | null>(null);
  const [validCode, setValidCode] = useState(true);

  useEffect(() => {
    if (inviteCode !== '') {
      axios
        .get(`/api/invite/${inviteCode}`)
        .then((response) => {
          const respInvite = response.data;
          setInvite(respInvite);

          if (respInvite.invitedUserId) {
            setValidCode(false);
          } else {
            const { days, hours } = secondsToDaysAndHours(
              respInvite.expires - Date.now() / 1000
            );

            if (days >= 0 && hours >= 0) {
              setExpiration({ days, hours });
              setInvitingUser(respInvite.invitingUser);
              if (respInvite.teamName) {
                setNameToManage(respInvite.teamName);
              } else if (respInvite.playerName) {
                setNameToManage(respInvite.playerName);
              }

              if (userStore.authUser) {
                signOut(false);
              }
            } else {
              setValidCode(false);
              if (userStore.authUser) {
                history.push('/');
              }
            }
          }
          toggleLoading(false);
        })
        .catch((e) => {
          Sentry.captureException(e);
          setError(e.toString());
          toggleLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    inviteType: invite?.teamRole,
    invitingUser: `${invitingUser?.nameFirst} ${invitingUser?.nameLast}`,
    error,
    expiration,
    invite,
    loading,
    nameToManage,
    validCode,
  };
};
